import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Container, Paper, Accordion, AccordionSummary,
  AccordionDetails, Stepper, Step, StepLabel, StepContent, Divider,
  List, ListItem, ListItemIcon, ListItemText, Chip, Alert, Card, CardContent,
  Grid, Tab, Tabs
} from '@mui/material';
import { useStore } from '../context/StoreContext';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PaymentIcon from '@mui/icons-material/Payment';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import GavelIcon from '@mui/icons-material/Gavel';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import VerifiedIcon from '@mui/icons-material/Verified';

// 公司資訊常數
const COMPANY_INFO = {
  name: '昀美國際貿易有限公司',
  taxId: '42762120',
  phone: '04-7380668',
  address: '彰化市彰南路三段481-1號',
  email: 'service@urstore.tw',
  lineId: '@454ygmfg',
  serviceHours: '週一至週五 9:00-18:00'
};

const ShoppingGuide = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tabValue, setTabValue] = useState(0);
  const { shippingConfig } = useStore();
  const freeShippingThreshold = shippingConfig?.free_shipping_threshold || 499;

  // 支援 URL 參數直接跳轉到特定頁籤
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab !== null) {
      const tabIndex = parseInt(tab);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 5) {
        setTabValue(tabIndex);
      }
    }
  }, [searchParams]);

  const handleTabChange = (e, newValue) => {
    setTabValue(newValue);
    setSearchParams({ tab: newValue.toString() });
  };

  // 購物流程步驟
  const shoppingSteps = [
    {
      label: '瀏覽商品',
      description: '瀏覽優爾商城的批發優惠商品，使用搜尋或分類篩選找到您想要的商品。每件商品都標示原價與特價，讓您清楚了解折扣幅度。'
    },
    {
      label: '加入購物車',
      description: '選擇商品規格與數量後，點擊「加入購物車」。您可以繼續購物或直接結帳。購物車內商品不會自動保留庫存，建議儘早完成結帳。'
    },
    {
      label: '會員登入/註冊',
      description: '結帳前需登入會員。支援手機號碼、Google 帳號或 LINE 快速登入，首次購物自動成為會員。'
    },
    {
      label: '填寫收件資訊',
      description: '選擇超商取貨門市或宅配地址。超商取貨請選擇 7-11 或全家門市，宅配請填寫完整地址。'
    },
    {
      label: '選擇付款方式',
      description: '提供超商取貨付款、LINE Pay、信用卡等多種付款方式。選擇適合您的付款方式完成結帳。'
    },
    {
      label: '完成訂單',
      description: '確認訂單資訊後送出。您會收到訂單確認通知，可在會員中心追蹤訂單狀態。'
    }
  ];

  // 付款方式
  const paymentMethods = [
    { name: '超商取貨付款', fee: '$0', desc: '到超商門市取貨時付款，最方便的付款方式', time: '取貨時付款' },
    { name: 'LINE Pay', fee: '$0', desc: '使用 LINE Pay 行動支付，支援信用卡與儲值金', time: '即時付款' },
    { name: '信用卡', fee: '$0', desc: '支援 VISA、MasterCard、JCB 等國際信用卡', time: '即時付款' },
    { name: '超商代碼繳費', fee: '$0', desc: '取得繳費代碼後，至超商繳款機繳費', time: '3天內繳費' }
  ];

  // 配送方式
  const shippingMethods = [
    { name: '7-11 超商取貨', fee: '$60', freeThreshold: `$${freeShippingThreshold}`, time: '3-5 個工作天', note: '門市保管 7 天' },
    { name: '全家超商取貨', fee: '$60', freeThreshold: `$${freeShippingThreshold}`, time: '3-5 個工作天', note: '門市保管 7 天' },
    { name: '宅配到府', fee: '$100', freeThreshold: '$799', time: '1-3 個工作天', note: '離島另計' }
  ];

  // 常見問題
  const faqItems = [
    {
      question: '商品可以退換貨嗎？',
      answer: '依據消費者保護法，您享有商品到貨後 7 天的猶豫期（非試用期）。若商品有瑕疵，請於收貨 3 日內聯繫客服辦理退換貨。'
    },
    {
      question: '如何追蹤我的訂單？',
      answer: '登入會員中心後，點選「我的訂單」即可查看所有訂單狀態。出貨後會收到包含物流追蹤碼的通知。'
    },
    {
      question: '超商取貨要多久？',
      answer: '一般約 3-5 個工作天送達指定門市。到貨後會以簡訊通知，請於 7 天內完成取貨，逾期將退回。'
    },
    {
      question: '可以修改訂單嗎？',
      answer: '訂單成立後如需修改，請盡快聯繫客服。若訂單已進入出貨流程，則無法修改。'
    },
    {
      question: '商品庫存是即時更新的嗎？',
      answer: '是的，商品庫存數量為系統即時顯示，售完後頁面會自動更新為已售完狀態。'
    },
    {
      question: '為什麼價格這麼便宜？',
      answer: '優爾商城提供批發價格優惠，包含各類優質商品，皆以批發價出售，品質有保障。'
    }
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        購物說明
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        歡迎來到優爾商城！以下為購物流程與相關說明，讓您購物更安心。
      </Typography>

      {/* 頁籤導航 */}
      <Paper sx={{ mb: 4 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<BusinessIcon />} label="關於我們" />
          <Tab icon={<ShoppingCartIcon />} label="購物流程" />
          <Tab icon={<PaymentIcon />} label="付款配送" />
          <Tab icon={<AssignmentReturnIcon />} label="退換貨政策" />
          <Tab icon={<GavelIcon />} label="服務條款" />
          <Tab icon={<PrivacyTipIcon />} label="隱私權政策" />
        </Tabs>
      </Paper>

      {/* 關於我們 */}
      {tabValue === 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon color="primary" />
            關於優爾商城
          </Typography>

          <Alert severity="success" icon={<VerifiedIcon />} sx={{ my: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold">合法登記公司・安心購物保障</Typography>
            我們是經濟部正式登記的合法公司，提供完善的消費者保障。
          </Alert>

          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
                    公司簡介
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    優爾商城是昀美國際貿易有限公司旗下的電商品牌，提供批發價格優惠商品。
                    我們秉持「優質商品・優惠價格」的理念，將各類優質商品以最優惠的批發價格提供給消費者。
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    所有商品皆為正品，來自知名品牌的過季庫存或包裝微損品，
                    讓您以超值價格購得高品質商品。我們承諾提供完善的售後服務與購物保障。
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%', bgcolor: '#f5f5f5' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
                    公司資訊
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><BusinessIcon color="primary" /></ListItemIcon>
                      <ListItemText
                        primary="公司名稱"
                        secondary={COMPANY_INFO.name}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><VerifiedIcon color="success" /></ListItemIcon>
                      <ListItemText
                        primary="統一編號"
                        secondary={COMPANY_INFO.taxId}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><PhoneIcon color="primary" /></ListItemIcon>
                      <ListItemText
                        primary="聯絡電話"
                        secondary={COMPANY_INFO.phone}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><LocationOnIcon color="primary" /></ListItemIcon>
                      <ListItemText
                        primary="公司地址"
                        secondary={COMPANY_INFO.address}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><EmailIcon color="primary" /></ListItemIcon>
                      <ListItemText
                        primary="客服信箱"
                        secondary={COMPANY_INFO.email}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" fontWeight="bold" gutterBottom>
            我們的優勢
          </Typography>
          <Grid container spacing={2}>
            {[
              { title: '正品保證', desc: '所有商品皆為品牌正品，非仿冒品' },
              { title: '超值價格', desc: '批發優惠價，最高可省 50% 以上' },
              { title: '快速出貨', desc: '下單後 1-2 個工作天內出貨' },
              { title: '安心付款', desc: '支援超商取貨付款，收到再付款' }
            ].map((item, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Card variant="outlined" sx={{ textAlign: 'center', p: 2 }}>
                  <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.desc}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* 購物流程 */}
      {tabValue === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCartIcon color="primary" />
            購物流程
          </Typography>
          <Stepper orientation="vertical" sx={{ mt: 3 }}>
            {shoppingSteps.map((step, index) => (
              <Step key={step.label} active={true}>
                <StepLabel>
                  <Typography variant="subtitle1" fontWeight="bold">{step.label}</Typography>
                </StepLabel>
                <StepContent>
                  <Typography color="text.secondary">{step.description}</Typography>
                </StepContent>
              </Step>
            ))}
          </Stepper>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" fontWeight="bold" gutterBottom>
            常見問題
          </Typography>
          {faqItems.map((faq, index) => (
            <Accordion key={index}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="medium">{faq.question}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary">{faq.answer}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
      )}

      {/* 付款配送 */}
      {tabValue === 2 && (
        <Box>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PaymentIcon color="primary" />
              付款方式
            </Typography>
            <Grid container spacing={2} sx={{ mt: 2 }}>
              {paymentMethods.map((method, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">{method.name}</Typography>
                        <Chip label={`手續費 ${method.fee}`} color="success" size="small" />
                      </Box>
                      <Typography variant="body2" color="text.secondary">{method.desc}</Typography>
                      <Typography variant="caption" color="primary">付款時間：{method.time}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalShippingIcon color="primary" />
              配送方式
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              滿額免運！超商取貨滿 ${freeShippingThreshold} 免運費，宅配滿 $799 免運費
            </Alert>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {shippingMethods.map((method, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold">{method.name}</Typography>
                      <Box sx={{ my: 1 }}>
                        <Chip label={`運費 ${method.fee}`} size="small" sx={{ mr: 1 }} />
                        <Chip label={`滿 ${method.freeThreshold} 免運`} color="error" size="small" />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        配送時間：{method.time}
                      </Typography>
                      <Typography variant="caption" color="warning.main">
                        {method.note}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Box>
      )}

      {/* 退換貨政策 */}
      {tabValue === 3 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssignmentReturnIcon color="primary" />
            退換貨政策
          </Typography>

          <Alert severity="info" sx={{ my: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold">退換貨說明</Typography>
            依據消費者保護法，您享有七日猶豫期權益。商品如有瑕疵，請於收貨後3日內聯繫客服處理。
          </Alert>

          <Typography variant="h6" fontWeight="bold" sx={{ mt: 3, mb: 2 }}>
            依據消費者保護法第19條
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
              <ListItemText
                primary="7天猶豫期"
                secondary="消費者得於收受商品後七日內，退回商品或以書面通知企業經營者解除買賣契約，無須說明理由及負擔任何費用或價款。"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><WarningIcon color="warning" /></ListItemIcon>
              <ListItemText
                primary="猶豫期非試用期"
                secondary="猶豫期並非試用期，商品必須保持全新狀態（未拆封、未使用），且包裝完整（含外盒、配件、贈品等），否則將影響退貨權益。"
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" fontWeight="bold" gutterBottom>
            不適用七天猶豫期之商品
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            依據消費者保護法第19條第1項但書規定，以下商品不適用七天猶豫期：
          </Typography>
          <List dense>
            {[
              '易於腐敗、保存期限較短或解約時即將逾期之商品',
              '依消費者要求所為之客製化給付',
              '報紙、期刊或雜誌',
              '經消費者拆封之影音商品或電腦軟體',
              '已拆封之個人衛生用品',
              '國際航空客運服務'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemIcon><InfoIcon fontSize="small" color="disabled" /></ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" fontWeight="bold" gutterBottom>
            商品瑕疵處理
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            若您收到的商品有瑕疵（非人為損壞），請於收貨後 3 日內聯繫客服，並提供以下資訊：
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="primary" fontSize="small" /></ListItemIcon>
              <ListItemText primary="訂單編號" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="primary" fontSize="small" /></ListItemIcon>
              <ListItemText primary="商品瑕疵照片（含外包裝）" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="primary" fontSize="small" /></ListItemIcon>
              <ListItemText primary="瑕疵說明" />
            </ListItem>
          </List>
          <Typography variant="body2" color="text.secondary">
            客服確認後將安排換貨或退款，運費由本公司負擔。
          </Typography>
        </Paper>
      )}

      {/* 服務條款 */}
      {tabValue === 4 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GavelIcon color="primary" />
            購物須知與服務條款
          </Typography>

          <Alert severity="info" sx={{ my: 2 }}>
            使用本網站服務即表示您同意以下條款，請詳細閱讀。
          </Alert>

          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="bold">一、會員資格與帳號安全</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List dense>
                <ListItem>
                  <ListItemText primary="1. 會員須年滿 18 歲或取得法定代理人同意方可註冊。" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="2. 會員應妥善保管帳號密碼，如有遭盜用情形應立即通知本公司。" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="3. 禁止將帳號借予他人使用或轉讓。" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="4. 如發現異常使用，本公司有權暫停或終止會員資格。" />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="bold">二、商品資訊</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List dense>
                <ListItem>
                  <ListItemText primary="1. 商品圖片僅供參考，實際商品以收到為準。" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="2. 清倉商品可能包含過季品、庫存品、外盒微損等情形，皆於商品頁面說明。" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="3. 商品價格與庫存以結帳時為準，本公司保留價格調整之權利。" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="4. 若遇缺貨情形，將主動聯繫消費者辦理退款或更換商品。" />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="bold">三、訂單與付款</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List dense>
                <ListItem>
                  <ListItemText primary="1. 訂單成立後，系統將發送確認通知至您的聯絡方式。" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="2. 付款期限依付款方式而定，逾期未付款訂單將自動取消。" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="3. 發票將於出貨後以電子發票方式寄送，如需統編請於結帳時填寫。" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="4. 本公司保留拒絕可疑訂單之權利。" />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="bold">四、智慧財產權</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary">
                本網站所有內容，包括但不限於文字、圖片、商標、版面設計等，均受著作權法及相關法律保護。
                未經本公司書面同意，不得擅自重製、公開傳輸或用於商業用途。
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="bold">五、免責聲明</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List dense>
                <ListItem>
                  <ListItemText primary="1. 因不可抗力因素（天災、系統故障等）造成之服務中斷，本公司不負賠償責任。" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="2. 消費者使用商品應遵循商品說明，因不當使用造成之損害，本公司不負責任。" />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              公司名稱：{COMPANY_INFO.name}<br />
              統一編號：{COMPANY_INFO.taxId}<br />
              聯絡電話：{COMPANY_INFO.phone}<br />
              公司地址：{COMPANY_INFO.address}<br />
              LINE 客服：{COMPANY_INFO.lineId}（加LINE咨詢）<br />
              客服信箱：{COMPANY_INFO.email}<br />
              服務時間：{COMPANY_INFO.serviceHours}
            </Typography>
          </Box>
        </Paper>
      )}

      {/* 隱私權政策 */}
      {tabValue === 5 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PrivacyTipIcon color="primary" />
            隱私權政策
          </Typography>

          <Alert severity="success" icon={<SecurityIcon />} sx={{ my: 2 }}>
            您的個人資料受到嚴格保護，我們遵循個人資料保護法相關規定。
          </Alert>

          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="bold">一、個人資料之蒐集</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" paragraph>
                本公司依據個人資料保護法及相關法令規定，蒐集您的個人資料，包括：
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon fontSize="small" color="primary" /></ListItemIcon>
                  <ListItemText primary="基本資料：姓名、電話、電子郵件" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon fontSize="small" color="primary" /></ListItemIcon>
                  <ListItemText primary="配送資料：收件地址、超商門市" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon fontSize="small" color="primary" /></ListItemIcon>
                  <ListItemText primary="交易資料：訂單紀錄、付款資訊" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon fontSize="small" color="primary" /></ListItemIcon>
                  <ListItemText primary="第三方登入資料：Google、LINE 帳號（僅基本資料）" />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="bold">二、個人資料之利用</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" paragraph>
                您的個人資料將用於以下目的：
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="1. 處理訂單及配送商品" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="2. 客戶服務與售後支援" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="3. 寄送電子報及行銷資訊（可隨時取消訂閱）" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="4. 改善網站服務及使用者體驗" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="5. 依法令規定或主管機關要求提供" />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="bold">三、個人資料之保護</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List dense>
                <ListItem>
                  <ListItemIcon><SecurityIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="採用 SSL 加密傳輸，確保資料傳輸安全" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><SecurityIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="資料庫存取權限管控，僅授權人員可存取" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><SecurityIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="定期進行資安檢測與更新" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><SecurityIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="不會將您的資料出售給第三方" />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="bold">四、Cookie 使用</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" paragraph>
                本網站使用 Cookie 技術以提供更好的服務體驗，包括：
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="• 記住您的登入狀態" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• 保存購物車內容" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• 分析網站使用情形以改善服務" />
                </ListItem>
              </List>
              <Typography variant="body2" color="text.secondary">
                您可透過瀏覽器設定拒絕 Cookie，但可能影響部分網站功能。
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="bold">五、您的權利</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" paragraph>
                依據個人資料保護法，您享有以下權利：
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="1. 查詢或請求閱覽您的個人資料" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="2. 請求製給複製本" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="3. 請求補充或更正" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="4. 請求停止蒐集、處理或利用" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="5. 請求刪除" />
                </ListItem>
              </List>
              <Typography variant="body2" color="text.secondary">
                如需行使上述權利，請聯繫客服：service@urstore.tw
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Box sx={{ mt: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
            <Typography variant="body2">
              本隱私權政策最後更新日期：2024 年 12 月<br />
              本公司保留修改隱私權政策之權利，修改後將公告於本頁面。
            </Typography>
          </Box>
        </Paper>
      )}

      {/* 客服資訊 */}
      <Paper sx={{ p: 3, mt: 4, bgcolor: '#fff8e1' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <SupportAgentIcon fontSize="large" color="primary" />
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="h6" fontWeight="bold">需要幫助？</Typography>
            <Typography variant="body2" color="text.secondary">
              聯絡電話：{COMPANY_INFO.phone}<br />
              LINE 客服：{COMPANY_INFO.lineId}（加LINE咨詢）<br />
              客服信箱：{COMPANY_INFO.email}<br />
              服務時間：{COMPANY_INFO.serviceHours}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">
              {COMPANY_INFO.name}<br />
              統編：{COMPANY_INFO.taxId}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default ShoppingGuide;
